import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import * as d3 from 'd3';
import Snackbar from '@mui/material/Snackbar';

// Import Components
import LoadDataHospital from '../../Func/LoadDataHospital';
import LoadPictureHospital from '../../Func/LoadPictureHospital';
import ModalMap from '../../Components/DialogModal/ModalMap';
import ModalPictureHospital from '../../Components/DialogModal/ModalPictureHospital';
import Navbar from '../../Components/Navbar/Navbar';
import Footer from '../../Components/Footer/Footer';
import HospitalCard from '../../Components/HospitalCard/HospitalCard';
import ProvinceModal from '../../Components/ProvinceModal/ProvinceModal';

// --- Main Component ---
const HemodialysisUnitNew = () => {
    const [selectedProvince, setSelectedProvince] = useState(null);
    const [hoverProvince, setHoverProvince] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showSnackbar, setShowSnackbar] = useState(false);
    
    // Modal states
    const [openModalMap, setOpenModalMap] = useState(false);
    const [openPicture, setOpenPicture] = useState(false);
    const [dataOnclick, setDataOnclick] = useState(null);
    
    const mapRef = useRef(null);
    const svgRef = useRef(null);
    const gRef = useRef(null);
    const zoomBehaviorRef = useRef(null);
    const dataClinic = LoadDataHospital();
    const dataPictureHospital = LoadPictureHospital(dataOnclick);

    const provinceNamesTH = useMemo(() => ({
        "Bangkok Metropolis": "กรุงเทพมหานคร", "Amnat Charoen": "อำนาจเจริญ", "Ang Thong": "อ่างทอง", 
        "Bueng Kan": "บึงกาฬ", "Buri Ram": "บุรีรัมย์", "Chachoengsao": "ฉะเชิงเทรา", "Chai Nat": "ชัยนาท", 
        "Chaiyaphum": "ชัยภูมิ", "Chanthaburi": "จันทบุรี", "Chiang Mai": "เชียงใหม่", "Chiang Rai": "เชียงราย", 
        "Chon Buri": "ชลบุรี", "Chumphon": "ชุมพร", "Kalasin": "กาฬสินธุ์", "Kamphaeng Phet": "กำแพงเพชร", 
        "Kanchanaburi": "กาญจนบุรี", "Khon Kaen": "ขอนแก่น", "Krabi": "กระบี่", "Lampang": "ลำปาง", 
        "Lamphun": "ลำพูน", "Loei": "เลย", "Lop Buri": "ลพบุรี", "Mae Hong Son": "แม่ฮ่องสอน", 
        "Maha Sarakham": "มหาสารคาม", "Mukdahan": "มุกดาหาร", "Nakhon Nayok": "นครนายก", 
        "Nakhon Pathom": "นครปฐม", "Nakhon Phanom": "นครพนม", "Nakhon Ratchasima": "นครราชสีมา", 
        "Nakhon Sawan": "นครสวรรค์", "Nakhon Si Thammarat": "นครศรีธรรมราช", "Nan": "น่าน", 
        "Narathiwat": "นราธิวาส", "Nong Bua Lam Phu": "หนองบัวลำภู", "Nong Khai": "หนองคาย", 
        "Nonthaburi": "นนทบุรี", "Pathum Thani": "ปทุมธานี", "Pattani": "ปัตตานี", "Phangnga": "พังงา", 
        "Phatthalung": "พัทลุง", "Phayao": "พะเยา", "Phetchabun": "เพชรบูรณ์", "Phetchaburi": "เพชรบุรี", 
        "Phichit": "พิจิตร", "Phitsanulok": "พิษณุโลก", "Phra Nakhon Si Ayutthaya": "พระนครศรีอยุธยา", 
        "Phrae": "แพร่", "Phuket": "ภูเก็ต", "Prachin Buri": "ปราจีนบุรี", "Prachuap Khiri Khan": "ประจวบคีรีขันธ์", 
        "Ranong": "ระนอง", "Ratchaburi": "ราชบุรี", "Rayong": "ระยอง", "Roi Et": "ร้อยเอ็ด", 
        "Sa Kaeo": "สระแก้ว", "Sakon Nakhon": "สกลนคร", "Samut Prakan": "สมุทรปราการ", 
        "Samut Sakhon": "สมุทรสาคร", "Samut Songkhram": "สมุทรสงคราม", "Saraburi": "สระบุรี", 
        "Satun": "สตูล", "Sing Buri": "สิงห์บุรี", "Si Sa Ket": "ศรีสะเกษ", "Songkhla": "สงขลา", 
        "Sukhothai": "สุโขทัย", "Suphan Buri": "สุพรรณบุรี", "Surat Thani": "สุราษฎร์ธานี", 
        "Surin": "สุรินทร์", "Tak": "ตาก", "Trang": "ตรัง", "Trat": "ตราด", "Ubon Ratchathani": "อุบลราชธานี", 
        "Udon Thani": "อุดรธานี", "Uthai Thani": "อุทัยธานี", "Uttaradit": "อุตรดิตถ์", "Yala": "ยะลา", 
        "Yasothon": "ยโสธร"
    }), []);

    const dialysisData = useMemo(() => {
        if (!Array.isArray(dataClinic)) return {};
        return dataClinic.filter(a => !!a.region).reduce((acc, item) => {
            const addressParts = item.codeAddress ? item.codeAddress.split(',').map(s => s.trim()) : [];
            const provinceTH = addressParts[2] || "";
            const provinceEN = Object.keys(provinceNamesTH).find(key => provinceNamesTH[key] === provinceTH) || provinceTH || "Unknown";
            if (!acc[provinceEN]) acc[provinceEN] = [];
            acc[provinceEN].push({
                hname: item.hname,
                hcode: item.hcode,
                address: `${item.address} ${item.codeAddress}`,
                codeAddress: item.codeAddress,
                phone: item.phone,
                map: item.map,
                type: item.region
            });
            return acc;
        }, {});
    }, [dataClinic, provinceNamesTH]);

    // Modal handlers
    const handleOpenModalMap = useCallback((data) => {
        setDataOnclick(data);
        setOpenModalMap(true);
    }, []);

    const handleCloseModalMap = () => {
        setDataOnclick(null);
        setOpenModalMap(false);
    };

    const handleOpenModalPicture = useCallback((data) => {
        setDataOnclick(data);
        setOpenPicture(true);
    }, []);

    const handleCloseModalPicture = () => {
        setDataOnclick(null);
        setOpenPicture(false);
    };

    // ฟังก์ชัน Update Style โดยไม่ Redraw
    const updateProvinceStyles = useCallback(() => {
        if (!gRef.current) return;

        d3.select(gRef.current).selectAll('.province-path')
            .transition()
            .duration(300)
            .style('fill', (d) => {
                const pName = d.properties.name;
                const isSelected = pName === selectedProvince?.name;
                const hasBranches = dialysisData[pName] && dialysisData[pName].length > 0;
                if (isSelected) return '#f97316';
                if (hasBranches) return '#fed7aa';
                return '#fff7ed';
            });

        d3.select(gRef.current).selectAll('.province-text')
            .transition()
            .duration(300)
            .style('fill', (d) => {
                const isSelected = d.properties.name === selectedProvince?.name;
                return isSelected ? '#fff' : '#ea580c';
            });
    }, [selectedProvince, dialysisData]);

    // วาดแผนที่ครั้งแรกครั้งเดียว
    useEffect(() => {
        const drawMap = async () => {
            try {
                const response = await fetch('https://raw.githubusercontent.com/apisit/thailand.json/master/thailand.json');
                const data = await response.json();

                const width = 600;
                const height = 800;

                const svg = d3.select(mapRef.current)
                    .append('svg')
                    .attr('viewBox', `0 0 ${width} ${height}`)
                    .attr('preserveAspectRatio', 'xMidYMid meet')
                    .attr('class', 'w-full h-full cursor-grab active:cursor-grabbing')
                    .style('touch-action', 'none');

                svgRef.current = svg.node();
                const g = svg.append('g');
                gRef.current = g.node();

                const projection = d3.geoMercator().fitSize([width, height], data);
                const path = d3.geoPath().projection(projection);

                const zoom = d3.zoom()
                    .scaleExtent([1, 12])
                    .on('zoom', (event) => {
                        g.attr('transform', event.transform);
                        g.selectAll('text').style('font-size', (d) => {
                            const name = provinceNamesTH[d.properties.name] || "";
                            const baseSize = name.length > 10 ? 3.5 : (name.length > 7 ? 4.5 : 5.5);
                            return `${baseSize / Math.sqrt(event.transform.k)}px`;
                        });
                    });

                zoomBehaviorRef.current = zoom;
                svg.call(zoom);

                // วาดจังหวัด
                g.selectAll('path')
                    .data(data.features)
                    .enter()
                    .append('path')
                    .attr('d', path)
                    .attr('class', 'province-path cursor-pointer')
                    .style('stroke', '#fdba74')
                    .style('stroke-width', '0.4')
                    .on('mouseover', function (event, d) {
                        setHoverProvince(provinceNamesTH[d.properties.name] || d.properties.name);
                        d3.select(this).style('filter', 'brightness(0.95)');
                    })
                    .on('mouseout', function () {
                        setHoverProvince(null);
                        d3.select(this).style('filter', null);
                    })
                    .on('click', (event, d) => {
                        setSelectedProvince({
                            name: d.properties.name,
                            nameTH: provinceNamesTH[d.properties.name] || d.properties.name
                        });
                    });

                // วาดชื่อจังหวัด
                g.selectAll('text')
                    .data(data.features)
                    .enter()
                    .append('text')
                    .attr('class', 'province-text pointer-events-none font-bold')
                    .attr('transform', (d) => `translate(${path.centroid(d)})`)
                    .attr('dy', '.35em')
                    .attr('text-anchor', 'middle')
                    .style('font-size', (d) => {
                        const name = provinceNamesTH[d.properties.name] || "";
                        if (name.length > 10) return '3.2px';
                        if (name.length > 7) return '4.2px';
                        return '5.5px';
                    })
                    .style('opacity', (d) => provinceNamesTH[d.properties.name] ? 0.9 : 0)
                    .text((d) => provinceNamesTH[d.properties.name] || "");

                setIsLoading(false);
            } catch (e) {
                console.error("Map Load Error", e);
            }
        };

        drawMap();
    }, [provinceNamesTH]); // รันครั้งเดียวตอนโหลด

    // อัปเดตสีเมื่อ State เปลี่ยน โดยไม่ Redraw
    useEffect(() => {
        updateProvinceStyles();
    }, [selectedProvince, dialysisData, updateProvinceStyles]);

    const handleZoomIn = () => {
        if (svgRef.current && zoomBehaviorRef.current) {
            d3.select(svgRef.current).transition().duration(300).call(zoomBehaviorRef.current.scaleBy, 1.5);
        }
    };

    const handleZoomOut = () => {
        if (svgRef.current && zoomBehaviorRef.current) {
            d3.select(svgRef.current).transition().duration(300).call(zoomBehaviorRef.current.scaleBy, 0.6);
        }
    };

    const handleResetZoom = () => {
        if (svgRef.current && zoomBehaviorRef.current) {
            d3.select(svgRef.current).transition().duration(500).call(zoomBehaviorRef.current.transform, d3.zoomIdentity);
        }
    };

    const copyInfo = (text) => {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        try {
            document.execCommand('copy');
            setShowSnackbar(true);
        } catch (err) {
            console.error('Copy failed', err);
        }
        document.body.removeChild(textArea);
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setShowSnackbar(false);
    };

    const currentCenters = useMemo(() => {
        if (!selectedProvince) return [];
        return dialysisData[selectedProvince.name] || [];
    }, [selectedProvince, dialysisData]);

    return (
        <>
            <Navbar />
            <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-blue-50 font-sans p-3 md:p-6 lg:p-8">
                <div className="mx-auto max-w-7xl flex flex-col h-full lg:min-h-[calc(100vh-4rem)]">
                    
                    {/* Header Section */}
                    <div className="mb-6 md:mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4">
                        <div className="flex-1">
                            <h1 className="text-2xl md:text-3xl lg:text-4xl font-black text-slate-800 tracking-tight">
                                ค้นหาศูนย์บริการ <span className="text-orange-500">ฟอกไต</span>
                            </h1>
                            <p className="text-slate-500 text-xs md:text-sm lg:text-base font-medium mt-1">
                                {selectedProvince ? `หน่วยบริการในจังหวัด${selectedProvince.nameTH}` : 'คลิกเลือกจังหวัด หรือซูมแผนที่เพื่อดูรายละเอียด'}
                            </p>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="flex-1 flex flex-col lg:flex-row gap-4 md:gap-6 lg:gap-8 min-h-0">
                        
                        {/* Map Section */}
                        <div className={`
                            transition-all duration-500 ease-in-out
                            bg-white rounded-3xl md:rounded-[3rem] border border-orange-100 shadow-sm overflow-hidden relative
                            flex flex-col items-center justify-center
                            ${selectedProvince
                                ? 'lg:w-[40%] h-[400px] md:h-[500px] lg:h-auto'
                                : 'w-full h-[600px] md:h-[800px] lg:h-auto'
                            }
                        `}>
                            {/* Map Header */}
                            <div className="absolute top-4 left-6 md:top-8 md:left-8 z-10 pointer-events-none">
                                <span className="text-[10px] font-black text-orange-400 uppercase tracking-[0.2em] block mb-1">Interactive Map</span>
                                <h2 className="text-lg md:text-2xl font-black text-slate-800 leading-tight">
                                    {hoverProvince || selectedProvince?.nameTH || 'แผนที่ประเทศไทย'}
                                </h2>
                            </div>

                            {/* Zoom Controls */}
                            <div className="absolute bottom-6 left-6 z-20 flex flex-col gap-2">
                                <button onClick={handleZoomIn} className="w-10 h-10 bg-white border border-slate-200 rounded-xl shadow-lg flex items-center justify-center font-bold text-slate-600 hover:bg-orange-500 hover:text-white transition-all">+</button>
                                <button onClick={handleZoomOut} className="w-10 h-10 bg-white border border-slate-200 rounded-xl shadow-lg flex items-center justify-center font-bold text-slate-600 hover:bg-orange-500 hover:text-white transition-all">-</button>
                                <button onClick={handleResetZoom} className="w-10 h-10 bg-slate-900 border border-slate-800 rounded-xl shadow-lg flex items-center justify-center text-white hover:bg-orange-600 transition-all">
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
                                </button>
                            </div>

                            {/* Legend */}
                            <div className="absolute top-4 right-6 md:top-8 md:right-8 z-10 flex flex-col gap-2 bg-white/80 p-3 rounded-2xl border border-orange-50 backdrop-blur-md shadow-sm">
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full bg-[#fed7aa] border border-orange-200"></div>
                                    <span className="text-[10px] font-bold text-slate-600">มีศูนย์บริการ</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full bg-[#fff7ed] border border-orange-100"></div>
                                    <span className="text-[10px] font-bold text-slate-400">ยังไม่มีข้อมูล</span>
                                </div>
                            </div>

                            {/* Loading Spinner */}
                            {isLoading && (
                                <div className="absolute inset-0 flex items-center justify-center bg-white/50 z-20">
                                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
                                </div>
                            )}

                            {/* Map Container */}
                            <div ref={mapRef} className="w-full h-full min-h-[400px]"></div>
                        </div>

                        {/* Province Modal - Using the separate component */}
                        <ProvinceModal
                            isOpen={!!selectedProvince}
                            onClose={() => setSelectedProvince(null)}
                            provinceName={selectedProvince?.nameTH || ''}
                            centers={currentCenters}
                            onOpenMap={handleOpenModalMap}
                            onOpenPicture={handleOpenModalPicture}
                            onCopy={copyInfo}
                        />
                    </div>
                </div>

                {/* Modal Map */}
                <ModalMap 
                    openModalMap={openModalMap} 
                    handleCloseModalMap={handleCloseModalMap} 
                    dataOnclick={dataOnclick} 
                />

                {/* Modal Picture */}
                <ModalPictureHospital 
                    openPicture={openPicture} 
                    handleCloseModalPicture={handleCloseModalPicture} 
                    dataPictureHospital={dataPictureHospital} 
                />

                {/* Snackbar for Copy Notification */}
                <Snackbar
                    open={showSnackbar}
                    autoHideDuration={2000}
                    onClose={handleCloseSnackbar}
                    message="คัดลอกข้อมูลเรียบร้อยแล้ว"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    ContentProps={{
                        sx: {
                            background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                            color: 'white',
                            fontWeight: 'bold',
                            borderRadius: '1rem',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                        }
                    }}
                />

                {/* Custom Styles */}
                <style dangerouslySetInnerHTML={{
                    __html: `
                        .province-path:hover { filter: brightness(0.95); }
                        .custom-scrollbar::-webkit-scrollbar { width: 5px; }
                        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                        .custom-scrollbar::-webkit-scrollbar-thumb { background: #ffedd5; border-radius: 10px; }
                        
                        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
                        @keyframes slide-in-from-right-8 { from { transform: translateX(2rem); } to { transform: translateX(0); } }
                        @keyframes slide-in-from-bottom-4 { from { transform: translateY(1rem); } to { transform: translateY(0); } }
                        
                        .animate-in { 
                            animation-duration: 0.5s;
                            animation-timing-function: ease-out;
                            animation-fill-mode: forwards;
                        }
                        
                        @media (max-width: 1023px) {
                            .animate-in { animation-name: fade-in, slide-in-from-bottom-4; }
                        }
                        @media (min-width: 1024px) {
                            .animate-in { animation-name: fade-in, slide-in-from-right-8; }
                        }
                    `
                }} />
            </div>
            <Footer />
        </>
    );
}

export default HemodialysisUnitNew;
